// Admin Users List Component
// Display list of users with stats and search functionality

import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { supabase } from '@/lib/supabase';
import { 
  Search, 
  User, 
  Mail, 
  Calendar, 
  Activity,
  MessageSquare,
  Briefcase,
  Eye
} from 'lucide-react';
import Icon from '@/components/ui/Icon';
import { toast, useToast } from '@/components/ui/use-toast';

const AdminUsersList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const { dismiss } = useToast(); // Get dismiss function
  const [selectedUser, setSelectedUser] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [pagination, setPagination] = useState({
    limit: 20,
    offset: 0,
    total: 0,
    hasMore: false
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async (searchTerm = search, offset = 0) => {
    try {
      setLoading(true);
      
      // Get current session to include auth headers
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }
      
      console.log('AdminUsersList: Session info', {
        hasSession: !!session,
        hasAccessToken: !!session?.access_token,
        tokenLength: session?.access_token?.length || 0,
        userEmail: session?.user?.email
      });
      
      const response = await fetch(
        `/api/admin/users?limit=${pagination.limit}&offset=${offset}&search=${encodeURIComponent(searchTerm)}`,
        {
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication required');
        } else if (response.status === 403) {
          throw new Error('Admin access required');
        } else {
          throw new Error(`Failed to load users: ${response.status}`);
        }
      }
      
      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON response received:', text);
        throw new Error('Invalid response format from server');
      }
      
      const data = await response.json();
      
      if (data.success) {
        setUsers(data.users);
        setPagination(data.pagination);
      } else {
        throw new Error(data.message || 'Failed to load users');
      }
    } catch (error) {
      console.error('Error loading users:', error);
      
      // Dismiss any existing toasts before showing new one
      dismiss();
      
      toast({
        title: "Error loading users",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    loadUsers(search, 0);
  };

  const handleViewDetails = async (user) => {
    try {
      // Get current session to include auth headers
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }
      
      const response = await fetch(`/api/admin/user/${user.id}`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication required');
        } else if (response.status === 403) {
          throw new Error('Admin access required');
        } else {
          throw new Error(`Failed to load user details: ${response.status}`);
        }
      }
      
      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON response received for user details:', text);
        throw new Error('Invalid response format from server');
      }
      
      const data = await response.json();
      
      if (data.success) {
        setSelectedUser(data.user);
        setIsDetailModalOpen(true);
      } else {
        throw new Error(data.message || 'Failed to load user details');
      }
    } catch (error) {
      console.error('Error loading user details:', error);
      
      // Dismiss any existing toasts before showing new one
      dismiss();
      
      toast({
        title: "Error loading user details",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActivityStatus = (lastActive) => {
    if (!lastActive) return 'inactive';
    
    const daysSince = Math.floor(
      (new Date() - new Date(lastActive)) / (1000 * 60 * 60 * 24)
    );
    
    if (daysSince <= 1) return 'active';
    if (daysSince <= 7) return 'recent';
    return 'inactive';
  };

  const getActivityColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'recent': return 'bg-yellow-100 text-yellow-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="loader-circle" size="xl" className="animate-spin text-brand-600" />
        <span className="ml-2 text-gray-600">Loading users...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search Bar */}
      <div className="flex gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Search by email or name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            className="pl-10"
          />
        </div>
        <Button onClick={handleSearch}>
          <Search className="w-4 h-4 mr-2" />
          Search
        </Button>
      </div>

      {/* Users List */}
      <div className="space-y-4">
        {users.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No users found matching your criteria.
          </div>
        ) : (
          users.map((user) => {
            const activityStatus = getActivityStatus(user.stats?.last_active);
            
            return (
              <div key={user.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                        {user.avatar_url ? (
                          <img 
                            src={user.avatar_url} 
                            alt={user.full_name || 'User'} 
                            className="w-full h-full rounded-full object-cover"
                          />
                        ) : (
                          <User className="w-5 h-5 text-brand-600" />
                        )}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {user.full_name || 'Unknown User'}
                        </h3>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Mail className="w-3 h-3" />
                          <span>{user.email}</span>
                        </div>
                      </div>
                      <Badge className={getActivityColor(activityStatus)}>
                        <Activity className="w-3 h-3 mr-1" />
                        {activityStatus}
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar className="w-4 h-4" />
                        <div>
                          <p className="text-xs text-gray-500">Joined</p>
                          <p className="font-medium">{formatDate(user.created_at)}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Activity className="w-4 h-4" />
                        <div>
                          <p className="text-xs text-gray-500">Last Active</p>
                          <p className="font-medium">{formatDate(user.stats?.last_active)}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <MessageSquare className="w-4 h-4" />
                        <div>
                          <p className="text-xs text-gray-500">Feedback</p>
                          <p className="font-medium">{user.stats?.feedback_submitted || 0}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Briefcase className="w-4 h-4" />
                        <div>
                          <p className="text-xs text-gray-500">Programs</p>
                          <p className="font-medium">{user.stats?.programs_tracked || 0}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewDetails(user)}
                    className="ml-4"
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    View
                  </Button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {pagination.total > pagination.limit && (
        <div className="flex items-center justify-between pt-4 border-t">
          <p className="text-sm text-gray-600">
            Showing {pagination.offset + 1} - {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total} users
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => loadUsers(search, pagination.offset - pagination.limit)}
              disabled={pagination.offset === 0 || loading}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => loadUsers(search, pagination.offset + pagination.limit)}
              disabled={!pagination.hasMore || loading}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* User Detail Modal */}
      <Dialog open={isDetailModalOpen} onOpenChange={setIsDetailModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>User Details</DialogTitle>
          </DialogHeader>
          
          {selectedUser && (
            <div className="space-y-6">
              {/* User Info */}
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center">
                  {selectedUser.avatar_url ? (
                    <img 
                      src={selectedUser.avatar_url} 
                      alt={selectedUser.full_name || 'User'} 
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    <User className="w-8 h-8 text-brand-600" />
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold">{selectedUser.full_name || 'Unknown User'}</h3>
                  <p className="text-gray-600">{selectedUser.email}</p>
                  <div className="flex gap-2 mt-2">
                    <Badge>ID: {selectedUser.id.slice(0, 8)}</Badge>
                    <Badge className={getActivityColor(getActivityStatus(selectedUser.stats?.last_active))}>
                      {getActivityStatus(selectedUser.stats?.last_active)}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 border rounded-lg">
                  <p className="text-sm text-gray-500">Total Feedback</p>
                  <p className="text-2xl font-bold">{selectedUser.feedback?.length || 0}</p>
                </div>
                <div className="p-4 border rounded-lg">
                  <p className="text-sm text-gray-500">Programs Tracked</p>
                  <p className="text-2xl font-bold">{selectedUser.programs?.length || 0}</p>
                </div>
                <div className="p-4 border rounded-lg">
                  <p className="text-sm text-gray-500">Friends</p>
                  <p className="text-2xl font-bold">{selectedUser.friends?.length || 0}</p>
                </div>
                <div className="p-4 border rounded-lg">
                  <p className="text-sm text-gray-500">Member Since</p>
                  <p className="text-lg font-semibold">{formatDate(selectedUser.created_at)}</p>
                </div>
              </div>

              {/* Recent Feedback */}
              {selectedUser.feedback && selectedUser.feedback.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3">Recent Feedback</h4>
                  <div className="space-y-2">
                    {selectedUser.feedback.slice(0, 5).map((feedback) => (
                      <div key={feedback.id} className="p-3 border rounded-lg bg-gray-50">
                        <div className="flex items-center justify-between mb-1">
                          <Badge variant="outline">{feedback.feedback_type}</Badge>
                          <span className="text-xs text-gray-500">{formatDate(feedback.created_at)}</span>
                        </div>
                        <p className="text-sm text-gray-700 line-clamp-2">{feedback.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsersList;
